/**
 * Git-friendly YAML workspace persistence (user-chosen directory).
 * Requires Tauri desktop for filesystem access.
 */
import { invoke, isTauri as coreIsTauri } from '@tauri-apps/api/core';
import { writeWorkspaceFiles } from './gitFileWriter.js';
import { normalizeSnapshot, mergeSnapshotField } from './localWorkspaceStore.js';
import {
  snapshotToFileMap,
  fileTreeToSnapshot,
  parseYamlFile,
  WORKSPACE_MANIFEST,
} from '../utils/yamlWorkspace/index.js';
import { findExposedSecretKeys } from '../utils/yamlWorkspace/serialize.js';
import { normalizeRootPath } from '../utils/yamlWorkspace/paths.js';
import { projectBlockFor } from '../scope/projectManifestBlock';
import { projectFromManifest } from '../scope/projectManifestText';

/** @type {Set<string>} */
const registeredRoots = new Set();

function isTauri() {
  try {
    return coreIsTauri();
  } catch {
    return typeof window !== 'undefined' && window.__TAURI__ !== undefined;
  }
}

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

export async function registerWorkspaceScope(rootPath) {
  const root = normalizeRootPath(rootPath);
  if (!root || !isTauri()) return false;
  if (registeredRoots.has(root)) return true;
  try {
    await invoke('register_workspace_scope', { rootPath: root });
    registeredRoots.add(root);
    return true;
  } catch (err) {
    console.error('[gitWorkspaceStore] Failed to register scope:', err);
    return false;
  }
}

async function readTextAbs(absPath) {
  const fs = await fsModule();
  return fs.readTextFile(absPath);
}

async function writeTextAbs(absPath, content) {
  const fs = await fsModule();
  const normalized = absPath.replace(/\\/g, '/');
  const dir = normalized.slice(0, normalized.lastIndexOf('/'));
  if (dir) {
    await fs.mkdir(dir, { recursive: true });
  }
  await fs.writeTextFile(absPath, content);
}

async function existsAbs(absPath) {
  try {
    const fs = await fsModule();
    return await fs.exists(absPath);
  } catch {
    return false;
  }
}

async function readDirAbs(absPath) {
  try {
    const fs = await fsModule();
    return await fs.readDir(absPath);
  } catch {
    return [];
  }
}

function joinPath(root, ...parts) {
  const base = normalizeRootPath(root);
  return [base, ...parts.filter(Boolean)].join('/');
}

async function readYamlAbs(absPath) {
  try {
    const text = await readTextAbs(absPath);
    return parseYamlFile(text);
  } catch {
    return null;
  }
}

/**
 * Read a workspace file that EXISTS (it was listed, or is a known path) and
 * record it in `problems` when it cannot be used: unreadable, not valid YAML,
 * or — with `needsId` — a document without its `id`.
 *
 * Why it matters (git-sync audit 01.10.2026): a file that silently loads as
 * nothing makes its entity MISSING from the snapshot, and the inbound apply
 * tombstones missing rows. A request YAML saved half-finished in VS Code, or
 * with a typo, deleted that request from Apiboo. Callers now refuse to apply a
 * snapshot that has problems (see `unreadableFiles`).
 */
async function readCheckedAbs(absPath, relPath, problems, { needsId = false } = {}) {
  let doc;
  try {
    doc = parseYamlFile(await readTextAbs(absPath));
  } catch {
    problems.push(relPath);
    return null;
  }
  if (needsId && (doc == null || typeof doc !== 'object' || doc.id == null || doc.id === '')) {
    problems.push(relPath);
    return null;
  }
  return doc;
}

/** Like readCheckedAbs, for an optional file: absent is fine, broken is a problem. */
async function readOptionalCheckedAbs(absPath, relPath, problems, opts) {
  if (!(await existsAbs(absPath))) return null;
  return readCheckedAbs(absPath, relPath, problems, opts);
}

/**
 * COL-06 remediation notice. Workspaces mirrored before the serializer split
 * secrets out have those values in tracked files — and, if the user pushed, in
 * their remote's history. Rewriting someone's git history for them is not this
 * code's decision to make, and silently rewriting the working tree would hide
 * the exposure rather than fix it. So the load path detects the old shape and
 * says so, once per file, naming the keys. The next save writes the new shape,
 * which removes the value from the working tree but not from history — hence
 * "rotate", not "we fixed it".
 */
const warnedSecretFiles = new Set();

function warnAboutCommittedSecrets({ globals, workspaceVars, environments }) {
  const docs = [
    ['variables/globals.yaml', globals],
    ['variables/workspace.yaml', workspaceVars],
    ...(environments || []).map((env) => [`environments/${env?.id ?? 'unknown'}.yaml`, env]),
  ];
  for (const [file, doc] of docs) {
    const keys = findExposedSecretKeys(doc);
    if (keys.length === 0 || warnedSecretFiles.has(file)) continue;
    warnedSecretFiles.add(file);
    console.warn(
      `[apiboo] ${file} in this git workspace still contains the values of variables ` +
        `you marked secret (${keys.join(', ')}). If this workspace has ever been pushed, ` +
        `treat those credentials as exposed and rotate them — they are in the repository's ` +
        `history and Apiboo will not rewrite that for you. Saving this workspace moves the ` +
        `values into an untracked *.local.yaml sidecar going forward.`
    );
  }
}

/**
 * Load full workspace snapshot from YAML directory.
 */
export async function loadGitSnapshot(rootPath) {
  const root = normalizeRootPath(rootPath);
  if (!root) return normalizeSnapshot(null);
  if (!isTauri()) {
    throw new Error('Git workspaces require Apiboo Desktop');
  }
  await registerWorkspaceScope(root);

  const manifestPath = joinPath(root, WORKSPACE_MANIFEST);
  const manifest = await readYamlAbs(manifestPath);
  if (!manifest) {
    throw new Error(`Not an Apiboo workspace (missing ${WORKSPACE_MANIFEST})`);
  }

  /** Files that exist but cannot be used — see readCheckedAbs. */
  const unreadable = [];
  const opt = (rel, opts) => readOptionalCheckedAbs(joinPath(root, rel), rel, unreadable, opts);

  const globals = (await opt('variables/globals.yaml')) || { variables: [] };
  const workspaceVars = (await opt('variables/workspace.yaml')) || { variables: [] };
  // Untracked sidecars holding the values the tracked files omit (COL-06).
  // Absent for a fresh clone by design — a teammate gets the variable NAMES
  // and supplies their own values.
  const globalsLocal = await opt('variables/globals.local.yaml');
  const workspaceVarsLocal = await opt('variables/workspace.local.yaml');

  const environments = [];
  const environmentsLocal = [];
  const envDir = joinPath(root, 'environments');
  if (await existsAbs(envDir)) {
    const envEntries = await readDirAbs(envDir);
    for (const entry of envEntries) {
      if (!entry.name?.endsWith('.yaml')) continue;
      // `<slug>.local.yaml` also ends in `.yaml`; it is a sidecar, not an
      // environment, and must not be loaded as one.
      const doc = await readCheckedAbs(joinPath(envDir, entry.name), `environments/${entry.name}`, unreadable, {
        needsId: true,
      });
      if (!doc) continue;
      if (entry.name.endsWith('.local.yaml')) environmentsLocal.push(doc);
      else environments.push(doc);
    }
  }

  warnAboutCommittedSecrets({ globals, workspaceVars, environments });

  const collections = [];
  const colRoot = joinPath(root, 'collections');
  if (await existsAbs(colRoot)) {
    const colDirs = await readDirAbs(colRoot);
    for (const dirEntry of colDirs) {
      if (!dirEntry.isDirectory) continue;
      const colSlug = dirEntry.name;
      const meta = await opt(`collections/${colSlug}/collection.yaml`, { needsId: true });
      if (!meta) continue;

      const reqFiles = [];
      const reqDir = joinPath(colRoot, colSlug, 'requests');
      if (await existsAbs(reqDir)) {
        const reqEntries = await readDirAbs(reqDir);
        for (const reqEntry of reqEntries) {
          if (!reqEntry.name?.endsWith('.yaml')) continue;
          const req = await readCheckedAbs(
            joinPath(reqDir, reqEntry.name),
            `collections/${colSlug}/requests/${reqEntry.name}`,
            unreadable,
            { needsId: true }
          );
          if (req) reqFiles.push(req);
        }
        // Preserve order from index if present
        if (Array.isArray(meta.requests) && meta.requests.length > 0) {
          const byId = new Map(reqFiles.map((r) => [String(r.id), r]));
          const ordered = meta.requests
            .map((idx) => byId.get(String(idx.id)))
            .filter(Boolean);
          const remaining = reqFiles.filter((r) => !ordered.some((o) => o.id === r.id));
          reqFiles.length = 0;
          reqFiles.push(...ordered, ...remaining);
        }
      }

      // Secret collection/folder variable values (untracked, review F1).
      const local = await opt(`collections/${colSlug}/collection.local.yaml`);
      collections.push({ meta, requests: reqFiles, local });
    }
  }

  const schemasRaw = await opt('schemas/tree.yaml');

  const result = normalizeSnapshot(
    fileTreeToSnapshot({
      manifest,
      globals,
      globalsLocal,
      workspaceVars,
      workspaceVarsLocal,
      environments,
      environmentsLocal,
      collections,
      schemas: schemasRaw,
    })
  );
  if (manifest?.id) result.id = manifest.id;
  const project = projectFromManifest(manifest?.project); // card 2.2
  if (project) result.project = project;
  // Not part of the snapshot data: callers that APPLY a snapshot must refuse
  // while this is non-empty (gitFlush.scheduleExternalSnapshotApply, the pull
  // path in useWorkspaces) — applying would delete the unreadable entities.
  result.unreadableFiles = unreadable;
  return result;
}

/**
 * Write full workspace snapshot to YAML directory.
 */
export async function saveGitSnapshot(rootPath, partial, options = {}) {
  const root = normalizeRootPath(rootPath);
  if (!root || !isTauri()) return;
  await registerWorkspaceScope(root);

  const current = await loadGitSnapshot(root).catch(() => normalizeSnapshot(null));
  const skipEmpty = options.skipEmptyArrays === true;

  const merged = normalizeSnapshot({
    ...current,
    ...partial,
    workspaceName:
      partial.workspaceName !== undefined && partial.workspaceName !== ''
        ? partial.workspaceName
        : current.workspaceName,
    collections: mergeSnapshotField(partial.collections, current.collections, {
      skipEmptyArrays: skipEmpty,
    }),
    globalVariables: mergeSnapshotField(partial.globalVariables, current.globalVariables, {
      skipEmptyArrays: skipEmpty,
    }),
    workspaceVariables: mergeSnapshotField(
      partial.workspaceVariables,
      current.workspaceVariables,
      { skipEmptyArrays: skipEmpty }
    ),
    environments: mergeSnapshotField(partial.environments, current.environments, {
      skipEmptyArrays: skipEmpty,
    }),
    schemas: (() => {
      if (partial.schemas === undefined) return current.schemas;
      const next = {
        folders: Array.isArray(partial.schemas.folders) ? partial.schemas.folders : [],
        items: Array.isArray(partial.schemas.items) ? partial.schemas.items : [],
      };
      if (skipEmpty && !next.folders.length && !next.items.length) return current.schemas;
      return next;
    })(),
    selectedEnvironmentId:
      partial.selectedEnvironmentId !== undefined
        ? partial.selectedEnvironmentId
        : current.selectedEnvironmentId,
  });

  const workspaceMeta = {
    id: partial.id || current.id,
    name: partial.workspaceName || current.workspaceName,
    project: projectBlockFor(partial.id || current.id), // card 2.2
  };

  // schemas/tree.yaml is emitted by snapshotToFileMap since Phase 4 — no
  // separate bolt-on write (bytes are identical to the old direct dump).
  const { files } = snapshotToFileMap(merged, workspaceMeta);
  // Changed files only, recorded as our own, orphans deleted — so a request
  // deleted in Apiboo stays deleted (it used to come back from its old file).
  // A file that did not load is kept: it is what the user is fixing.
  await writeWorkspaceFiles(await fsModule(), root, files, { keep: current.unreadableFiles || [] });
}

/**
 * Export snapshot to a new YAML directory (creates all files).
 */
export async function exportSnapshotToDirectory(rootPath, snapshot, workspaceMeta = {}) {
  const root = normalizeRootPath(rootPath);
  if (!root || !isTauri()) {
    throw new Error('Export requires Apiboo Desktop');
  }
  await registerWorkspaceScope(root);
  const normalized = normalizeSnapshot(snapshot);
  const { files } = snapshotToFileMap(normalized, workspaceMeta);
  for (const [relPath, content] of Object.entries(files)) {
    await writeTextAbs(joinPath(root, relPath), content);
  }
}

/**
 * Check if path contains a valid apiboo.yaml workspace.
 */
export async function isGitWorkspaceRoot(rootPath) {
  const root = normalizeRootPath(rootPath);
  if (!root || !isTauri()) return false;
  await registerWorkspaceScope(root);
  return existsAbs(joinPath(root, WORKSPACE_MANIFEST));
}

/**
 * Per-file incremental save (Stage 2) — only writes changed entities.
 */
export async function saveGitSnapshotIncremental(rootPath, partial, options = {}) {
  // Full rewrite for now when collections change; Stage 2 optimization uses same entry point
  return saveGitSnapshot(rootPath, partial, options);
}
